import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/BrandMark";
import { LayoutDashboard, LogOut } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export function Navbar() {
  const { user, openAuth, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav data-testid="site-header" className="fixed top-0 inset-x-0 z-50 glass border-b border-subtle-border/10">
      <div className="max-w-7xl mx-auto px-3 sm:px-8 h-16 flex items-center justify-between gap-2">
        <Link to="/" data-testid="nav-logo" aria-label="ClippedUp home" className="flex items-center gap-2 group">
          <BrandMark testId="brand-mark-header" className="h-8 w-8 shrink-0 motion-safe:group-hover:scale-105 transition-transform" />
          <span data-testid="brand-wordmark" className="hidden min-[360px]:inline font-display text-base sm:text-xl font-extrabold tracking-tight text-slate-900 dark:text-white">Clipped<span className="text-cyan-500">Up</span></span>
        </Link>

        <div className="hidden md:flex items-center gap-8 text-sm text-copy-400">
          <a data-testid="nav-how-link" href="/#how" className="hover:text-foreground transition-colors">How it works</a>
          <a data-testid="nav-features-link" href="/#features" className="hover:text-foreground transition-colors">Features</a>
          <a data-testid="nav-pricing-link" href="/#pricing" className="hover:text-foreground transition-colors">Pricing</a>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
          <ThemeToggle />
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="flex items-center gap-2 outline-none">
                  <Avatar className="h-9 w-9 border border-surface-700">
                    <AvatarFallback className="bg-surface-800 text-cyan-400 text-sm font-semibold">
                      {(user.name || user.email)[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-surface-900 border-surface-800 text-foreground w-56">
                <div className="px-2 py-1.5 text-xs text-copy-500 truncate">{user.email}</div>
                <DropdownMenuSeparator className="bg-surface-800" />
                <DropdownMenuItem data-testid="nav-dashboard-link" onClick={() => navigate("/dashboard")}
                  className="cursor-pointer focus:bg-surface-800">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-logout-btn" onClick={() => { logout(); navigate("/"); }}
                  className="cursor-pointer focus:bg-surface-800 text-red-400">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button data-testid="nav-login-btn" variant="ghost" onClick={() => openAuth("login")}
                className="text-copy-300 hover:text-foreground hover:bg-surface-800 rounded-full px-2 sm:px-4">
                Log in
              </Button>
              <Button data-testid="nav-signup-btn" onClick={() => openAuth("register")}
                className="rounded-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold px-3 sm:px-4">
                Start free
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
